// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Appearance > Sidebar (desktop only). "Hide the sidebar behind the
// Konvo logo" tucks the icon rail away; hovering or clicking the logo in the
// top-left corner unrolls it over the page. Kept per device. Below it, Pages
// picks which page buttons the sidebar shows and their order (account-synced).

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {ClockIcon, SidebarSimpleIcon} from '@app/features/ui/icons/Icons';
import HiddenSidebar from '@app/features/ui/state/HiddenSidebar';
import {SidebarPagesEditor} from '@app/features/user/components/modals/tabs/appearance_tab/SidebarPagesEditor';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const HIDE_BEHIND_LOGO_DESCRIPTOR = msg({
	message: 'Hide the sidebar behind the Konvo logo',
	comment: 'Appearance setting (switch): the desktop icon sidebar is tucked away behind a logo in the top-left corner.',
});
const HIDE_BEHIND_LOGO_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Hover to open the sidebar. Click the logo to keep it open temporarily; click again to release it. Saved on this device.',
	comment: 'Description under the Hide the sidebar behind the Konvo logo switch in Appearance settings.',
});
const PIN_DURATION_DESCRIPTOR = msg({message: 'Keep sidebar open for'});
const PIN_DURATION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'How long the sidebar stays open after clicking the logo, even when switching pages. Saved on this device.',
});
const PIN_DURATION_OPTIONS = [
	{value: 15, label: msg({message: '15 seconds'})},
	{value: 30, label: msg({message: '30 seconds'})},
	{value: 60, label: msg({message: '1 minute'})},
	{value: 120, label: msg({message: '2 minutes'})},
	{value: 300, label: msg({message: '5 minutes'})},
	{value: 600, label: msg({message: '10 minutes'})},
];

export const SidebarTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const toggle = () => HiddenSidebar.setEnabled(!HiddenSidebar.enabled);
	return (
		<>
			<div className={kitStyles.rows} data-flx="user.appearance-tab.sidebar-tab.rows">
				<SettingsRow
					icon={<SidebarSimpleIcon size={20} />}
					title={i18n._(HIDE_BEHIND_LOGO_DESCRIPTOR)}
					description={i18n._(HIDE_BEHIND_LOGO_DESCRIPTION_DESCRIPTOR)}
					onActivate={toggle}
					control={({labelId, descriptionId}) => (
						<Switch
							compact
							value={HiddenSidebar.enabled}
							onChange={(value) => HiddenSidebar.setEnabled(value)}
							ariaLabelledBy={labelId}
							ariaDescribedBy={descriptionId}
							data-flx="user.appearance-tab.sidebar-tab.switch.hide-behind-logo"
						/>
					)}
					data-flx="user.appearance-tab.sidebar-tab.row.hide-behind-logo"
				/>
				{HiddenSidebar.enabled && (
					<SettingsRow
						icon={<ClockIcon size={20} />}
						title={i18n._(PIN_DURATION_DESCRIPTOR)}
						description={i18n._(PIN_DURATION_DESCRIPTION_DESCRIPTOR)}
						control={() => (
							<Combobox<number>
								value={HiddenSidebar.pinDurationSeconds}
								onChange={HiddenSidebar.setPinDurationSeconds}
								options={PIN_DURATION_OPTIONS.map(({value, label}) => ({value, label: i18n._(label)}))}
								isSearchable={false}
								density="compact"
								aria-label={i18n._(PIN_DURATION_DESCRIPTOR)}
								data-flx="user.appearance-tab.sidebar-tab.pin-duration"
							/>
						)}
						data-flx="user.appearance-tab.sidebar-tab.row.pin-duration"
					/>
				)}
			</div>
			<SidebarPagesEditor data-flx="user.appearance-tab.sidebar-tab.pages" />
		</>
	);
});
